import { Avatar, Box, styled } from '@mui/material'
import IndeterminateCheckBoxOutlined from '@mui/icons-material/IndeterminateCheckBoxOutlined'
import { CardContent, EditableCardItem, SelectOption } from '../../Kanban'
import { getOpenWbsItemDetailSpec } from '../../../../../lib/functions/wbsItem'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getPathByExternalId } from '../../..'
import {
  FunctionLayer,
  pushFunctionLayer,
} from '../../../../../store/functionLayer'
import { UserAvatarsGroup } from './UserAvatarsGroup'
import { useDispatch } from 'react-redux'
import TaskActualWorkDialog from '../../../../components/dialogs/TaskActualWorkDialog'
import { DisplayNameItem } from './DisplayNameItem'
import { ProjectPlanPath } from './ProjectPlanPath'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { KanbanProgressIcon } from '../../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus/KanbanProgressIcon'
import KanbanContext from '../../KanbanContext'
import Tooltip from '../../../../components/tooltips/Tooltip'
import { colorPalette } from '../../../../style/colorPalette'
import { roundNumber } from '../../../../../utils/number'

export type Props = {
  content: CardContent
  onChange?: (id: string, field: EditableCardItem, changed: any) => void
  onCloseWbsItemDetail?: (wbsItemCode: string) => void
}

export const WbsStatusCardContent = memo(
  ({ content, onCloseWbsItemDetail }: Props) => {
    const intl = useIntl()
    const dispatch = useDispatch()
    const [isOpenActulaWorkDialog, closeActulaWorkDialog] = useDialogState()
    const { priorities } = useContext(KanbanContext)

    const openDetailInLayer = useCallback(
      (layer: FunctionLayer) => {
        dispatch(
          pushFunctionLayer({
            ...layer,
            onClose: async () => {
              onCloseWbsItemDetail?.(layer.code!)
            },
          })
        )
      },
      [dispatch, onCloseWbsItemDetail]
    )

    const onClickWbsItemIcon = useCallback(
      async (event: any) => {
        const openDetailSpec = await getOpenWbsItemDetailSpec(true, content)
        const layer = openDetailSpec.layer

        if (event && (event['ctrlKey'] || event['metaKey'])) {
          window.open(`${getPathByExternalId(layer.externalId)}/${layer.code!}`)
          return
        }
        openDetailInLayer(layer)
      },
      [content, openDetailInLayer]
    )

    const accountable = useMemo(
      () =>
        content.accountable
          ? ({
              ...content.accountable,
              value: content.accountable?.uuid,
            } as SelectOption)
          : null,
      [content.accountable]
    )
    const responsible = useMemo(
      () =>
        content.responsible
          ? ({
              ...content.responsible,
              value: content.responsible?.uuid,
            } as SelectOption)
          : null,
      [content.responsible]
    )
    const assignee = useMemo(
      () =>
        content.assignee
          ? ({
              ...content.assignee,
              value: content.assignee?.uuid,
            } as SelectOption)
          : null,
      [content.assignee]
    )
    const priority = useMemo(
      () =>
        content.priority
          ? priorities.find(p => p.value === content.priority)
          : null,
      [priorities, content.priority]
    )

    return (
      <>
        <ProjectPlanPath
          path={content.path}
          parentDisplayName={content.parentWbsItem?.displayName}
          code={content.code}
        />
        <DisplayNameItem
          value={content.displayName}
          wbsItemType={content.wbsItemType!}
          onClickIcon={onClickWbsItemIcon}
        />
        <RowArea
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            mt: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <KanbanProgressIcon wbsItem={content} fontSize={24} />
            {priority?.iconUrl ? (
              <Tooltip
                message={`${intl.formatMessage({ id: 'popper.priority' })}${priority.name}`}
              >
                <Avatar
                  src={priority.iconUrl}
                  sx={{ width: 20, height: 20, margin: 0 }}
                />
              </Tooltip>
            ) : (
              <Tooltip
                message={intl.formatMessage({ id: 'popper.priority.unset' })}
              >
                <IndeterminateCheckBoxOutlined
                  sx={{
                    width: 20,
                    height: 20,
                    color: colorPalette.monotone[3],
                  }}
                />
              </Tooltip>
            )}
            {((content.estimatedWorkload?.hour ?? 0) > 0 ||
              (content.actualHour ?? 0) > 0) && (
              <Tooltip
                message={intl.formatMessage(
                  { id: 'popper.workload.tooltip' },
                  {
                    actual: roundNumber(content.actualHour ?? 0, 2),
                    scheduled: content.estimatedWorkload?.getFixedHour() ?? 0,
                  }
                )}
              >
                <Box
                  sx={{
                    fontSize: 12,
                    color: colorPalette.monotone[4],
                    paddingTop: '2px',
                    border: `1px solid ${colorPalette.monotone[3]}`,
                    borderRadius: '4px',
                    padding: '0 4px',
                    marginLeft: '4px',
                  }}
                >
                  {roundNumber(content.actualHour ?? 0, 2)}h /{' '}
                  {content.estimatedWorkload?.getFixedHour() ?? 0}h
                </Box>
              </Tooltip>
            )}
          </Box>
          <UserAvatarsGroup
            accountable={accountable}
            responsible={responsible}
            assignee={assignee}
          />
        </RowArea>
        {isOpenActulaWorkDialog && (
          <TaskActualWorkDialog
            open={true}
            taskUuid={content.uuid ?? ''}
            closeHandler={() => closeActulaWorkDialog()}
          />
        )}
      </>
    )
  },
  (prev, next) => prev.content === next.content
)

const RowArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
  width: '100%',
})
